import { useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useEffectOnce } from 'react-use';
import { Button } from '@c2fo/liquidity';
import { TechnicalDifficultyIcon } from '@c2fo/liquidity/icons';
import { GetMeQuery } from '@/generated/gql/graphql';
import NetworkIcon from '@/enterprise/assets/NetworkIcon';
import NoDataState from '@/enterprise/components/NoDataState';
import Skeleton from '@/enterprise/components/Skeleton';
import { useNavigationArtifacts } from '@/enterprise/data/useArtifacts';
import useFundboxOpportunity from '@/enterprise/data/useFundboxOpportunity';
import {
  FbxEligibilityStatus,
  getFundboxPageContent,
} from '@/enterprise/features/fundbox/content/fundboxSharedContent';
import FundboxApplicationSteps from '@/enterprise/features/fundbox/FundboxApplicationSteps';
import FundboxFuseEmbed from '@/enterprise/features/fundbox/FundboxFuseEmbed';
import FundboxKeyBenefits from '@/enterprise/features/fundbox/FundboxKeyBenefits';
import useFundboxFuseEmbed, {
  isFundboxFuseEmbedOpen,
  type FundboxFuseEmbedStatus,
} from '@/enterprise/features/fundbox/fuse/useFundboxFuseEmbed';
import { selectFundboxNavigationArtifact } from '@/enterprise/features/fundbox/selectFundboxNavigationArtifact';
import useFeature from '@/lib/features';
import { useReporting } from '@/reporting';
import NavigateWithRedirect from '@/shared/components/NavigateWithRedirect';
import useUserDetails from '@/shared/data/useUserDetails';
import { asEnterpriseLink } from '@/utils/experienceLinkHelper';

const CREDIT_LINE_DISCLAIMER =
  '*Credit line amounts, rates, and terms are based on creditworthiness and subject to change; additional documentation may be required.';

const FUNDING_DISCLAIMER =
  'Funding is provided by Fundbox, an external third-party provider. Fundbox makes capital available to businesses through business loans originated by Lead Bank. California businesses: Fundbox makes business loans and lines of credit loans pursuant to California Financing Law license 60DBO-48774. All loans are subject to credit approval of a completed application and applicable terms and conditions. Approval timing is based on typical experience; actual decision and funding times may vary. Financing may not be available in all states and may be subject to local restrictions where applicable.';

const fundboxErrorState = {
  title: 'Error loading Term Loans by Fundbox',
  message: 'Try refreshing or contact Customer Support if the problem persists.',
} as const;

const FundboxPageSkeleton = (): JSX.Element => (
  <div className="mx-auto max-w-full space-y-20 md:max-w-7xl">
    <Skeleton height="h-64 md:h-80" className="rounded-2xl" />
    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
      <Skeleton height="h-40" />
      <Skeleton height="h-40" />
      <Skeleton height="h-40" />
    </div>
    <Skeleton height="h-48" className="rounded-2xl" />
  </div>
);

interface FundboxReadyPageProps {
  status: FbxEligibilityStatus;
  displayAmount?: string;
  embedStatus: FundboxFuseEmbedStatus;
  fuseConfig: ReturnType<typeof useFundboxFuseEmbed>['fuseConfig'];
  mountRef: ReturnType<typeof useFundboxFuseEmbed>['mountRef'];
  openEmbed: () => void;
}

const FundboxReadyPage = ({
  status,
  displayAmount,
  embedStatus,
  fuseConfig,
  mountRef,
  openEmbed,
}: FundboxReadyPageProps): JSX.Element => {
  const isApproved = status === FbxEligibilityStatus.APPROVED;
  const content = getFundboxPageContent(displayAmount);
  const { trackEnterpriseEvent } = useReporting();
  const [searchParams, setSearchParams] = useSearchParams();
  const autoOpenHandledRef = useRef(false);

  const openEmbedWithTracking = (trigger: 'clicked' | 'link' | 'status'): void => {
    trackEnterpriseEvent('fundbox-marketing-page::embed::opened', {
      status,
      trigger,
      ...(trigger !== 'status' && content.ctaLabel ? { ctaLabel: content.ctaLabel } : {}),
    });
    openEmbed();
  };

  useEffectOnce(() => {
    // Make sure we only open the embed once, if ref is defined, we've already opened the embed
    if (autoOpenHandledRef.current) {
      return;
    }

    autoOpenHandledRef.current = true;

    if (isApproved) {
      openEmbedWithTracking('status');
    } else if (searchParams.get('openFundbox') === 'true' && content.ctaLabel) {
      openEmbedWithTracking('link');
    }

    if (searchParams.get('openFundbox') === 'true') {
      searchParams.delete('openFundbox');
      setSearchParams(searchParams, { replace: true });
    }
  });

  const handleCtaClick = (): void => {
    openEmbedWithTracking('clicked');
  };

  const showEmbed = isFundboxFuseEmbedOpen(embedStatus) && fuseConfig;
  const showEmbedError = embedStatus === 'error';

  return (
    <div className="mx-auto max-w-full md:max-w-7xl">
      {showEmbed ? (
        <div className="h-[calc(100vh-8rem)]">
          <FundboxFuseEmbed fuseConfig={fuseConfig} mountRef={mountRef} />
        </div>
      ) : showEmbedError ? (
        <div className="overflow-hidden rounded-2xl bg-white p-8 shadow-md md:p-12">
          <NoDataState
            icon={<TechnicalDifficultyIcon className="h-24 w-24" />}
            title={fundboxErrorState.title}
            message={fundboxErrorState.message}
            className="space-y-4"
            action={
              // Retry after a failed embed — not openEmbedWithTracking, which is for the initial open.
              // openEmbed logs fundbox-fuse::refetch-after-error when embedStatus is already error.
              <Button size="sm" variant="ancillary" onClick={() => openEmbed()}>
                Refresh
              </Button>
            }
          />
        </div>
      ) : isApproved ? (
        <FundboxPageSkeleton />
      ) : (
        <div className="relative space-y-20">
          <div className="grid grid-cols-1 items-center gap-4 rounded-2xl bg-secondary-500 px-8 py-6 shadow-md md:grid-cols-3 md:px-16 md:py-12">
            <div className="col-span-1 row-start-2 space-y-6 text-white md:col-span-2 md:row-start-1">
              <p className="text-4xl">{content.headline}</p>
              <p className="text-2xl">{content.subheadline}</p>
              {content.supportingLine ? <p className="text-base">{content.supportingLine}</p> : null}
              {content.ctaLabel ? (
                <Button variant="primary" inverse onClick={handleCtaClick}>
                  {content.ctaLabel}
                </Button>
              ) : null}
            </div>
            <div className="col-span-1 flex justify-center">
              <NetworkIcon
                primaryFill="#ffffff"
                secondaryFill="currentColor"
                className="h-64 text-primary-500"
                aria-hidden
              />
            </div>
          </div>

          <FundboxKeyBenefits />
          <FundboxApplicationSteps />
        </div>
      )}

      <div
        className={`space-y-4 border-t border-deepGray-500/10 pt-6 text-sm text-text-secondary ${
          showEmbed || isApproved || showEmbedError ? 'mt-6' : 'mt-20'
        }`}
      >
        <p>{CREDIT_LINE_DISCLAIMER}</p>
        <p>{FUNDING_DISCLAIMER}</p>
      </div>
    </div>
  );
};

const Fundbox = (): JSX.Element => {
  const { data: accountId } = useUserDetails(useCallback((user: GetMeQuery) => user.me?.account?.id, []));
  const [fundboxEnabled] = useFeature('enterprise-ui_enableFundbox');
  const { data: navigationArtifact, isPending: isAccessPending } = useNavigationArtifacts(
    accountId,
    (artifacts) => selectFundboxNavigationArtifact(artifacts) ?? null
  );
  const {
    status,
    displayAmount,
    isPending: isOpportunityPending,
    isError: isOpportunityError,
    refetch: refetchOpportunity,
  } = useFundboxOpportunity();
  const { embedStatus, fuseConfig, mountRef, openEmbed } = useFundboxFuseEmbed();

  if (fundboxEnabled && isAccessPending) {
    return <FundboxPageSkeleton />;
  }

  if (!fundboxEnabled || !navigationArtifact) {
    return <NavigateWithRedirect to={asEnterpriseLink('homepage')} />;
  }

  if (isOpportunityPending) {
    return <FundboxPageSkeleton />;
  }

  if (isOpportunityError) {
    return (
      <div className="mx-auto max-w-full md:max-w-7xl">
        <div className="overflow-hidden rounded-2xl bg-white p-8 shadow-md md:p-12">
          <NoDataState
            icon={<TechnicalDifficultyIcon className="h-24 w-24" />}
            title={fundboxErrorState.title}
            message={fundboxErrorState.message}
            className="space-y-4"
            action={
              <Button size="sm" variant="ancillary" onClick={refetchOpportunity}>
                Refresh
              </Button>
            }
          />
        </div>
      </div>
    );
  }

  if (!status) {
    return <FundboxPageSkeleton />;
  }

  return (
    <FundboxReadyPage
      status={status}
      displayAmount={displayAmount}
      embedStatus={embedStatus}
      fuseConfig={fuseConfig}
      mountRef={mountRef}
      openEmbed={openEmbed}
    />
  );
};

export default Fundbox;
